/**
 * PlayerOverlay 元件
 * 
 * 播放器覆蓋層，包括：
 * - 時間碼顯示
 * - 浮水印（未來）
 * - 載入指示器（未來）
 * 
 * @author AI Assistant
 * @date 2025-11-02
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

// 時間碼位置類型
export type TimecodePosition = 'topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight';

// Props 接口
export interface PlayerOverlayProps {
  // 時間碼相關
  currentTimecode: string;
  showTimecode: boolean;
  showTimecodeBackground: boolean;
  timecodePosition: TimecodePosition;
  timecodeScale?: number; // 🔧 新增：時間碼縮放比例
  timecodeElRef?: React.RefObject<HTMLElement | null>; // 🔧 新增：時間碼 DOM ref（用於高性能 rAF 更新）
  
  // 樣式控制
  isPortrait?: boolean;
  containerWidth?: number;
  containerHeight?: number;
}

/**
 * 獲取時間碼位置樣式
 */
const getTimecodePositionStyle = (position: TimecodePosition): React.CSSProperties => {
  const margin = 16; // 統一使用 16px 邊距

  switch (position) {
    case 'topLeft':
      return {
        top: margin,
        left: margin,
      };
    case 'topCenter':
      return {
        top: margin,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'topRight':
      return {
        top: margin,
        right: margin,
      };
    case 'bottomLeft':
      return {
        bottom: margin,
        left: margin,
      };
    case 'bottomCenter':
      return {
        bottom: margin,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'bottomRight':
    default:
      return {
        bottom: margin,
        right: margin,
      };
  }
};

/**
 * PlayerOverlay 元件
 * 
 * 顯示在播放器上方的覆蓋層，包含時間碼等資訊
 * 
 * @example
 * ```tsx
 * <PlayerOverlay
 *   currentTimecode="00:05:30 15"
 *   showTimecode={true}
 *   showTimecodeBackground={true}
 *   timecodePosition="bottomRight"
 * />
 * ```
 */
const PlayerOverlay: React.FC<PlayerOverlayProps> = React.memo(({
  currentTimecode,
  showTimecode,
  showTimecodeBackground,
  timecodePosition,
  timecodeScale = 1.0, // 🔧 新增：預設縮放比例為 1.0
  timecodeElRef, // 🔧 新增：接收 ref
  isPortrait = false,
}) => {
  // 如果不顯示時間碼，返回 null
  if (!showTimecode) {
    return null;
  }

  const positionStyle = getTimecodePositionStyle(timecodePosition);
  
  // 根據縮放比例和方向計算字體大小
  const baseFontSize = isPortrait ? 1.2 : 1.5; // rem
  const scaledFontSize = `${baseFontSize * timecodeScale}rem`;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      <Typography
        variant="h6"
        ref={timecodeElRef as any} // 🔧 新增：綁定 ref，讓 rAF 可以直接操作 DOM
        sx={{
          position: 'absolute',
          ...positionStyle,
          fontFamily: 'monospace',
          fontSize: scaledFontSize,
          fontWeight: 700,
          color: '#FFFFFF',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
          backgroundColor: showTimecodeBackground ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
          padding: showTimecodeBackground ? '4px 12px' : '0',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          transition: 'all 0.3s ease',
        }}
      >
        {currentTimecode}
      </Typography>
    </Box>
  );
}, (prevProps, nextProps) => {
  // 🔧 自定義比較函數：只在這些 props 變化時才重新渲染
  // currentTimecode 的變化會透過 ref 直接更新 DOM，不需要重新渲染
  return (
    prevProps.showTimecode === nextProps.showTimecode &&
    prevProps.showTimecodeBackground === nextProps.showTimecodeBackground &&
    prevProps.timecodePosition === nextProps.timecodePosition &&
    prevProps.timecodeScale === nextProps.timecodeScale &&
    prevProps.isPortrait === nextProps.isPortrait
    // 🔧 注意：故意不比較 currentTimecode，因為它會透過 ref 更新
  );
});

PlayerOverlay.displayName = 'PlayerOverlay';

export default PlayerOverlay;

